import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, ExternalLink, Save, Loader2, Check } from "lucide-react";
import DashboardShell from "@/pages/dashboard/DashboardShell";
import { monetize, PRODUCT_LABELS, PRODUCT_KINDS, PRODUCT_ICONS, formatPrice } from "@/lib/monetize";
import { extractFormError } from "@/lib/api";

const KIND_PLACEHOLDERS = {
  download: "Example: an ebook on aftercare for my medspa's Botox clients - what to avoid, what to expect, how to feel confident about their first appointment.",
  course: "Example: a 6-week self-paced course on local SEO for restaurant owners - modules on Google Business, review flywheels, and content that ranks.",
  membership: "Example: a monthly community for early-stage founders in Boston - two live office hours, a private channel, and monthly template drops.",
  booking: "Example: a 60-minute growth strategy session where I help agency owners map their next 90 days.",
  lead_magnet: "Example: a free 3-page reset guide for exhausted founders - written in a warm, non-preachy voice.",
};

function AIProductGenerator({ storefrontId, onCreated }) {
  const [kind, setKind] = useState("download");
  const [prompt, setPrompt] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const doDraft = async () => {
    if (prompt.trim().length < 10) {
      toast.error("Tell the Studio a little more about what you want to sell.");
      return;
    }
    setDrafting(true);
    setDraft(null);
    try {
      const r = await monetize.aiGenerate({ kind, prompt: prompt.trim() });
      setDraft(r.draft);
      toast.success("Draft ready. Review below and save to your storefront.");
    } catch (err) {
      toast.error(extractFormError(err, "The Studio is taking a moment. Try again."));
    } finally {
      setDrafting(false);
    }
  };

  const doSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const p = await monetize.createProduct(storefrontId, {
        kind,
        title: draft.title || "Untitled",
        tagline: draft.tagline || "",
        description: draft.description || "",
        price_cents: draft.price_cents || 0,
        interval: draft.interval || "one_time",
        cover_emoji: draft.cover_emoji || PRODUCT_ICONS[kind],
        config: draft,
      });
      toast.success(`${p.title} added.`);
      setDraft(null);
      setPrompt("");
      onCreated(p);
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't save product."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="monetize-ai" className="border border-gold/25 bg-gold/[0.04] p-6 sm:p-7">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-gold" strokeWidth={1.6} />
        <p className="overline">AI Product Generator</p>
      </div>
      <h3 className="font-serif text-xl mt-3">
        Describe it. <span className="italic text-aurora">We&apos;ll draft the entire product.</span>
      </h3>

      <div className="flex flex-wrap gap-2 mt-5">
        {PRODUCT_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => { setKind(k); setDraft(null); }}
            data-testid={`ai-kind-${k}`}
            aria-pressed={kind === k}
            className={`text-xs uppercase tracking-[0.18em] px-3 py-2 border transition ${
              kind === k ? "border-gold text-gold bg-gold/10" : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
            }`}
          >
            {PRODUCT_ICONS[k]} {PRODUCT_LABELS[k]}
          </button>
        ))}
      </div>

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={KIND_PLACEHOLDERS[kind]}
        rows={4}
        maxLength={1500}
        data-testid="ai-prompt"
        className="mt-4 w-full bg-white border border-neutral-200 focus:border-gold focus:outline-none p-4 text-sm leading-relaxed resize-y"
      />
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-neutral-400">{prompt.length} / 1500</span>
        <button
          type="button"
          onClick={doDraft}
          disabled={drafting}
          data-testid="ai-draft-cta"
          className="btn-aurora disabled:opacity-60"
        >
          {drafting ? <><Loader2 size={13} className="animate-spin" /> Drafting...</> : <><Sparkles size={13} /> Draft this product</>}
        </button>
      </div>

      {draft && (
        <div data-testid="ai-draft-result" className="mt-6 border-t border-neutral-200 pt-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="text-4xl">{draft.cover_emoji || PRODUCT_ICONS[kind]}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">{PRODUCT_LABELS[kind]} · {formatPrice(draft.price_cents || 0, "USD", draft.interval || "one_time")}</p>
              <h4 className="font-serif text-2xl mt-1.5 leading-snug">{draft.title}</h4>
              {draft.tagline && <p className="text-neutral-500 text-sm mt-2">{draft.tagline}</p>}
              {draft.description && <p className="text-neutral-500 text-sm mt-3 leading-relaxed">{draft.description}</p>}
            </div>
          </div>
          {draft.outline && Array.isArray(draft.outline) && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Outline</p>
              <ul className="mt-2 space-y-1.5">
                {draft.outline.slice(0, 8).map((o, i) => (
                  <li key={i} className="text-sm text-neutral-600"><span className="text-gold mr-2">·</span>{o.heading || o}</li>
                ))}
              </ul>
            </div>
          )}
          {draft.modules && Array.isArray(draft.modules) && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Modules ({draft.modules.length})</p>
              <ul className="mt-2 space-y-1.5">
                {draft.modules.map((m, i) => (
                  <li key={i} className="text-sm text-neutral-600"><span className="text-gold mr-2">{i + 1}.</span>{m.title}</li>
                ))}
              </ul>
            </div>
          )}
          {draft.perks && Array.isArray(draft.perks) && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Perks</p>
              <ul className="mt-2 space-y-1.5">
                {draft.perks.map((p, i) => (
                  <li key={i} className="text-sm text-neutral-600"><Check size={12} className="text-gold inline mr-2" />{p}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            type="button"
            onClick={doSave}
            disabled={saving}
            data-testid="ai-save-cta"
            className="btn-aurora disabled:opacity-60"
          >
            {saving ? <><Loader2 size={13} className="animate-spin" /> Saving...</> : <><Save size={13} /> Add to storefront</>}
          </button>
        </div>
      )}
    </div>
  );
}

function ProductRow({ p, onDelete, onTogglePublish }) {
  return (
    <div data-testid={`mn-prd-${p.id}`} className="border border-neutral-200 bg-white p-5 flex items-start gap-4">
      <div className="text-3xl mt-1">{p.cover_emoji || PRODUCT_ICONS[p.kind]}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{PRODUCT_LABELS[p.kind]}</p>
          <span className="text-[10px] text-neutral-500">·</span>
          <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{formatPrice(p.price_cents, p.currency, p.interval)}</p>
        </div>
        <h4 className="font-serif text-lg mt-1 leading-snug">{p.title}</h4>
        {p.tagline && <p className="text-sm text-neutral-500 mt-1 leading-relaxed">{p.tagline}</p>}
      </div>
      <div className="flex items-center gap-3 flex-none">
        <button
          onClick={() => onTogglePublish(p)}
          data-testid={`mn-prd-toggle-${p.id}`}
          className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 transition ${
            p.published ? "text-emerald-300 border-emerald-400/40" : "text-neutral-400 border-neutral-200"
          }`}
        >
          {p.published ? "Live" : "Draft"}
        </button>
        <button
          onClick={() => onDelete(p)}
          data-testid={`mn-prd-delete-${p.id}`}
          className="text-neutral-400 hover:text-rose-300"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export default function MonetizeStorefront() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [showAI, setShowAI] = useState(false);

  const load = async () => {
    try {
      const d = await monetize.getStorefront(id);
      setData(d);
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't load storefront."));
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePublish = async (p) => {
    try {
      const updated = await monetize.patchProduct(p.id, { published: !p.published });
      setData((d) => ({ ...d, products: d.products.map((x) => (x.id === p.id ? updated : x)) }));
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't update product."));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await monetize.deleteProduct(p.id);
      setData((d) => ({ ...d, products: d.products.filter((x) => x.id !== p.id) }));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't delete."));
    }
  };

  const toggleStorefrontPublish = async () => {
    try {
      const sf = await monetize.patchStorefront(id, { published: !data.storefront.published });
      setData((d) => ({ ...d, storefront: sf }));
      toast.success(sf.published ? "Storefront live." : "Storefront hidden.");
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't update storefront."));
    }
  };

  if (!data) {
    return (
      <DashboardShell back="/dashboard/monetize" backLabel="Monetization Hub">
        <p className="text-neutral-500 text-sm">Loading storefront...</p>
      </DashboardShell>
    );
  }
  const { storefront: sf, products } = data;

  return (
    <DashboardShell back="/dashboard/monetize" backLabel="Monetization Hub">
      <section data-testid="mn-sf-hero" className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="overline">Storefront</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-4">{sf.title}</h1>
          {sf.tagline && <p className="text-neutral-500 mt-3 max-w-xl">{sf.tagline}</p>}
          <p className="text-xs text-neutral-500 mt-4 font-mono">goldkey.studio/store/{sf.slug}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={toggleStorefrontPublish}
            data-testid="mn-sf-publish"
            className="btn-ghost"
          >
            {sf.published ? "Unpublish" : "Publish"}
          </button>
          <a
            href={`/store/${sf.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="mn-sf-view"
            className="btn-aurora"
          >
            View live <ExternalLink size={12} />
          </a>
        </div>
      </section>

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <p className="overline">Products ({products.length})</p>
          <button
            type="button"
            onClick={() => setShowAI((v) => !v)}
            data-testid="mn-toggle-ai"
            className="text-xs uppercase tracking-[0.18em] text-gold hover:text-gold"
          >
            {showAI ? "Hide AI generator" : "+ AI Product Generator"}
          </button>
        </div>
        {showAI && <div className="mb-6"><AIProductGenerator storefrontId={id} onCreated={(p) => setData((d) => ({ ...d, products: [p, ...d.products] }))} /></div>}
        <div className="space-y-3">
          {products.length === 0 && (
            <p className="text-sm text-neutral-500 border border-dashed border-neutral-200 p-6">No products yet. Open the AI generator to create your first one in seconds.</p>
          )}
          {products.map((p) => (
            <ProductRow key={p.id} p={p} onDelete={del} onTogglePublish={togglePublish} />
          ))}
        </div>
      </section>

      <section className="mt-14 border-t border-neutral-200/70 pt-8">
        <p className="overline">Coming next</p>
        <p className="text-sm text-neutral-500 mt-2 max-w-2xl leading-relaxed">
          Affiliate tracking + email/SMS drip sequences are wired at the API level and roll out to the UI in Phase 2. To enable SMS flows now, connect a Twilio API key in Admin → Integrations.
        </p>
      </section>
    </DashboardShell>
  );
}
